<template>
  <section>
    <trek-loading page :is-loading="isConfirmLoading" />
    <fieldset class="form-fieldset my-2">
      <legend
        class="form-fieldset__legend text-lg font-bold"
        qaid="feedback-product-details__type-label"
        v-html="bp('productType')"
      />
      <label for="Bikes" class="radio">
        <input
          id="Bikes"
          v-model="selectedProductType"
          type="radio"
          value="Bikes"
          qaid="feedback-product-details__type-bike"
          checked
          @change="productTypeChanged"
        />
        <span class="control-indicator"></span>
        {{ bp('bikes') }}
      </label>
      <label for="parts-and-accessories" class="radio">
        <input
          id="parts-and-accessories"
          v-model="selectedProductType"
          type="radio"
          value="PartsAndAccessories"
          qaid="feedback-product-details__type-parts"
          @change="productTypeChanged"
        />
        <span class="control-indicator"></span>
        {{ bp('partsAccessories') }}
      </label>
    </fieldset>

    <hr class="rule my-4" />

    <div class="grid-x grid-margin-x">
      <div v-if="isPartsAndAccessories" key="inputName('sku')" class="cell large-7">
        <div class="form-group" :class="{'has-error': !!errors.first(inputName('sku'))}">
          <label for="sku" class="form-label" qaid="feedback-product-details__sku-label">{{ bp('sku') }} </label>
          <div class="form-group--inline is-required" :class="hasWarning">
            <input
              id="sku"
              v-model="claimLine.sku"
              type="text"
              :maxlength="25"
              class="form-control flex-grow"
              qaid="feedback-product-details__sku"
              :data-vv-as="bp('sku')"
              :name="inputName('sku')"
            />
            <trek-button
              primary
              form-inline
              class="md:ml-1"
              qaid="feedback-product-detail__lookup"
              @click="processSerialOrSku"
              v-html="bp('skuLookup')"
            />
          </div>
          <div v-show="errors.first(inputName('sku'))" key="sku_form_warning" class="form-feedback is-invalid">
            {{ errors.first(inputName('sku')) }}
          </div>
          <div
            v-if="emptyInput"
            key="sku_empty_warning"
            class="form-feedback block text-warning"
            :class="isVisible(emptyInput)"
            v-html="bp('enterSKU')"
          ></div>
          <div
            v-if="showSkuNotFound"
            key="sku_warning"
            class="form-feedback block text-warning"
            :class="isVisible(showSkuNotFound)"
            v-html="bp('tryAnother')"
          ></div>
        </div>

        <trek-button v-if="!showProductDetails" secondary qaid="unknown-sku-button" @click="processNoInput">
          <span v-html="bp('tryAnotherSku')" />
        </trek-button>
      </div>

      <div v-if="isBike" :key="inputName('serialNumber')" class="cell large-7">
        <div class="form-group" :class="{'has-error': !!errors.first(inputName('serialNumber'))}">
          <label for="serial-number" class="form-label" qaid="warranty-claim-line-details-serial-label"
            >{{ bp('serialNumber') }}
            <span class="text-red ml-25">*</span>
          </label>

          <div class="form-group--inline is-required" :class="hasWarning">
            <input
              id="serial-number"
              v-model="claimLine.serialNumber"
              v-validate="'required'"
              type="text"
              required
              class="form-control flex-grow"
              qaid="feedback-product-details__serial"
              :data-vv-as="bp('serialNumber')"
              :name="inputName('serialNumber')"
              :maxlength="30"
              @blur="updateWarningMessages"
            />

            <trek-button primary form-inline class="md:ml-1" @click="processSerialOrSku">
              <span v-html="bp('serialLookup')" />
            </trek-button>
          </div>
          <div v-show="errors.first(inputName('serialNumber'))" class="form-feedback is-invalid">
            {{ errors.first(inputName('serialNumber')) }}
          </div>
          <div
            v-if="showSerialNotFound"
            key="serialNumber_warning"
            class="form-feedback block text-warning"
            :class="isVisible(showSerialNotFound)"
            v-html="bp('tryAnother')"
          ></div>
        </div>
      </div>

      <div v-if="showProductDetails" class="cell large-5">
        <form-group
          v-model="claimLine.modelName"
          type="text"
          qaid="feedback-product-details__model"
          :name="inputName('model')"
          :error="errors.first(inputName('model'))"
          :show-error-messages="true"
          :data-vv-as="bp('model')"
          :label="bp('model')"
        />
        <div class="grid-x grid-margin-x">
          <form-group
            v-if="isBike"
            v-model="claimLine.sku"
            class="cell large-5"
            type="text"
            :maxlength="25"
            qaid="feedback-product-details__sku"
            :name="inputName('sku')"
            :error="errors.first(inputName('sku'))"
            :show-error-messages="true"
            :data-vv-as="bp('sku')"
            :label="bp('sku')"
          />
          <form-group
            v-if="isBike"
            v-model="claimLine.modelYear"
            class="cell large-7"
            type="select"
            qaid="feedback-product-details__year"
            :name="inputName('modelYear')"
            :multi-items="modelYearList"
            :error="errors.first(inputName('modelYeashowr'))"
            :show-error-messages="true"
            :data-vv-as="bp('year')"
            :label="bp('year')"
          />
        </div>
        <div class="grid-x grid-margin-x">
          <form-group
            v-model="claimLine.color"
            class="cell large-7"
            type="text"
            qaid="feedback-product-details__color"
            :name="inputName('color')"
            :error="errors.first(inputName('color'))"
            :show-error-messages="true"
            :data-vv-as="bp('color')"
            :label="bp('color')"
          />

          <form-group
            v-model="claimLine.size"
            class="cell large-5"
            type="text"
            qaid="feedback-product-details__size"
            :name="inputName('size')"
            :error="errors.first(inputName('size'))"
            :show-error-messages="true"
            :data-vv-as="bp('size')"
            :label="bp('size')"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import axios from 'axios';
import {mapActions, mapState} from 'vuex';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import TrekButton from '@/components/TrekButton';
import FormGroup from '@/components/FormGroup';
import TrekLoading from '@/components/TrekLoading';

export default {
  inject: ['$validator'],
  components: {TrekButton, FormGroup, TrekLoading},
  props: {
    baseProperties: {
      type: Array,
      default: () => [],
    },
    feedbackData: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      isConfirmLoading: false,
      showSerialNotFound: false,
      showSkuNotFound: false,
      showProductDetails: false,
      validator: null,
      emptySerialInput: false,
      serialValidated: false,
      selectedProductType: 'Bikes',
      emptyInput: false,
    };
  },

  computed: {
    ...mapState('productFeedback', ['validators']),
    modelYearList() {
      const modelYearList = [];
      if (this.claimLine && this.claimLine.modelYearList) {
        modelYearList.push({value: null, displayName: null});
        this.claimLine.modelYearList.map((year) => {
          modelYearList.push({value: year, displayName: year});
        });
      }
      return modelYearList;
    },
    hasWarning() {
      return this.emptyInput || this.showSerialNotFound || this.showSkuNotFound ? 'has-warning' : '';
    },
    isPartsAndAccessories() {
      return this.claimLine && this.claimLine.productType === 'PartsAndAccessories';
    },
    isBike() {
      return this.claimLine && this.claimLine.productType === 'Bikes';
    },
    claimLine() {
      return get(this.feedbackData, 'claimLines[0]', {});
    },
    isEmptySku() {
      return this.isPartsAndAccessories && this.claimLine.sku === '';
    },
    isemptySerial() {
      return this.isBike && this.claimLine.serialNumber === '';
    },
  },

  mounted() {
    this.validators.push(this.$validator);

    if (
      this.claimLine &&
      ((this.isBike && this.claimLine.serialNumber) || (this.isPartsAndAccessories && this.claimLine.sku))
    ) {
      this.serialValidated = true;
    }
  },
  methods: {
    isVisible(flag) {
      return flag ? 'block' : '';
    },
    inputName(propertyName) {
      return `cl_${this.claimLine.productType}_${propertyName}`;
    },
    updateWarningMessages() {
      this.$validator.validateAll().then(() => {
        this.clearWarningMessages();
      });
    },
    productTypeChanged() {
      this.showSerialNotFound = false;
      this.showSkuNotFound = false;
      this.emptyInput = false;
      this.claimLine.sku = '';
      this.updateFlag({flagName: 'showFeedbackDetails', value: false});
      this.updateFeedbackProperty({
        propName: 'productType',
        value: this.selectedProductType,
      });
      this.$validator.reset();
      this.showProductDetails = false;
      this.validators.push(this.$validator);
    },
    clearWarningMessages() {
      this.showSerialNotFound = false;
      this.showSkuNotFound = false;
    },
    clearFormInputs() {
      this.claimLine.modelName = '';
      if (this.isBike) {
        this.claimLine.modelYear = '';
      }
      this.claimLine.size = '';
      this.claimLine.color = '';
      this.serialValidated = false;
      this.showSkuNotFound = false;
      this.$forceUpdate();
    },
    processNoInput() {
      // Clear old fields
      this.clearFormInputs();
      this.showProductDetails = true;
      this.updateFlag({flagName: 'showFeedbackDetails', value: true});
    },
    processSerialOrSku() {
      // need to run validation for serial lookup
      this.$validator.validateAll().then(() => {
        this.clearFormInputs();
        if (this.isEmptySku) {
          this.emptyInput = true;
          return;
        }
        if ((this.isBike && this.claimLine.serialNumber) || (this.isPartsAndAccessories && this.claimLine.sku)) {
          let urlType = this.isBike ? 'serial' : 'sku';
          let param = this.isBike ? this.claimLine.serialNumber : this.claimLine.sku;
          this.isConfirmLoading = true;
          axios
            .get(`/claims/${urlType}/${param}/`)
            .then((response) => {
              this.handleResponse(response.data.data, response.data.meta);
            })
            .finally(() => {
              this.isConfirmLoading = false;
            });
          if (this.isBike) {
            this.claimLine.sku = '';
          }
        }
      });
    },
    handleResponse(response, meta) {
      if (!isEmpty(response)) {
        this.showProductDetails = true;
        this.updateFeedbackProperty({propName: 'modelName', value: response.modelName});
        if (this.isBike) {
          this.updateFeedbackProperty({propName: 'sku', value: response.sku});
          this.updateFeedbackProperty({propName: 'modelYear', value: response.modelYear});
          this.updateFeedbackProperty({propName: 'modelYearList', value: response.modelYearList});
        }
        this.updateFeedbackProperty({propName: 'color', value: response.color});
        this.updateFeedbackProperty({propName: 'size', value: response.size});
        this.updateFlag({flagName: 'showFeedbackDetails', value: true});
        this.serialValidated = true;
        this.emptyInput = false;
        this.showSkuNotFound = false;
        // Force update since we're modifying deeper attributes than what Vue is watching for
        this.$forceUpdate();
      }
      if (!isEmpty(meta.feedback.message)) {
        if (meta.feedback.message.basePropertyKey === 'warrantyClaims.B2B.unableLocateSerial') {
          this.showSerialNotFound = true;

          this.serialValidated = false;
          this.showSkuNotFound = false;
        } else if (meta.feedback.message.basePropertyKey === 'warrantyClaims.B2B.unableLocateSku') {
          this.showSerialNotFound = false;
          this.showSkuNotFound = true;
        }
      }
    },
    ...mapActions('productFeedback', ['setFeedbackData', 'updateFeedbackProperty', 'updateFlag']),
  },
};
</script>
